import { IResult, ISignalPhaseInfo } from "@/types/map";
import { loadModules } from "esri-loader";
import CrossSignalPhase from "./CrossSignalPhase";

export default class SignalPhaseController {
  private static intances: Map<string, SignalPhaseController>;

  public static getInstance(view: __esri.SceneView) {
    const id = view.container.id;
    if (!SignalPhaseController.intances) {
      SignalPhaseController.intances = new Map();
    }
    let instance = SignalPhaseController.intances.get(id);
    if (!instance) {
      instance = new SignalPhaseController(view);
      SignalPhaseController.intances.set(id, instance);
    }
    return instance;
  }

  private view!: __esri.SceneView;
  private arrowLayer!: __esri.GraphicsLayer;
  private sidewalkLayer!: __esri.FeatureLayer;
  private countdownLabelLayer!: __esri.FeatureLayer;

  private initialized = false;

  private crossSignalPhaseMap: Map<string, CrossSignalPhase> = new Map();

  public requestNextSignalPhase!: (crossId: string) => void;
  public phaseChanged!: (crossId: string, phase: string) => void;

  private constructor(view: __esri.SceneView) {
    this.view = view;
  }

  private async initialize() {
    type MapModules = [
      typeof import("esri/layers/GraphicsLayer"),
      typeof import("esri/layers/FeatureLayer")
    ];
    const [GraphicsLayer, FeatureLayer] = await (loadModules([
      "esri/layers/GraphicsLayer",
      "esri/layers/FeatureLayer",
    ]) as Promise<MapModules>);

    this.arrowLayer = new GraphicsLayer();
    this.countdownLabelLayer = new FeatureLayer({
      source: [],
      objectIdField: "OBJECTID",
      geometryType: "point",
      outFields: ["*"],
      spatialReference: this.view.spatialReference,
      fields: [
        { name: "OBJECTID", type: "oid" },
        { name: "CROSSED", type: "string" },
        { name: "countdown", type: "integer" },
        { name: "color", type: "string" },
      ],
      renderer: {
        type: "simple", // autocasts as new SimpleRenderer()
        symbol: {
          type: "point-3d", // autocasts as new PointSymbol3D()
          symbolLayers: [
            {
              type: "icon", // autocasts as new IconSymbol3DLayer()
              resource: {
                primitive: "circle",
              },
              material: {
                color: [255, 255, 255, 0],
              },
              size: 4,
            },
          ],
        },
      } as any,
      labelingInfo: [
        {
          labelPlacement: "above-center",
          labelExpressionInfo: {
            expression: "$feature.countdown",
          },
          symbol: {
            type: "label-3d",
            symbolLayers: [
              {
                type: "text",
                material: {
                  color: "white",
                },
                halo: {
                  color: "black",
                  size: 2,
                },
                size: 18,
              },
            ],
            verticalOffset: {
              screenLength: 8,
              maxWorldLength: 15,
              minWorldLength: 5,
            },
            callout: {
              type: "line",
              size: 1,
              color: [0, 0, 0],
              border: {
                color: [255, 255, 255, 0.7],
              },
            },
          } as any,
        },
      ],
    });
    this.view.map.addMany([this.arrowLayer, this.countdownLabelLayer]);

    // 显示相位时将服务端的人行横道图层隐藏，改为由客户端控制
    this.sidewalkLayer = this.view.map.allLayers.find(
      (layer) => layer.title === "人行横道"
    ) as __esri.FeatureLayer;

    this.initialized = true;
  }

  public async setSignalPhase(params: ISignalPhaseInfo): Promise<IResult> {
    if (!this.initialized) {
      await this.initialize();
    }

    const crossId = params.crossId;
    let crossSignalPhase: CrossSignalPhase;
    if (!this.crossSignalPhaseMap.has(crossId)) {
      crossSignalPhase = new CrossSignalPhase({
        view: this.view,
        crossId,
        arrowLayer: this.arrowLayer,
        labelLayer: this.countdownLabelLayer,
      });
      crossSignalPhase.requestNextSignalPhase = this.requestNextSignalPhase;
      crossSignalPhase.phaseChanged = this.phaseChanged;
      this.crossSignalPhaseMap.set(crossId, crossSignalPhase);

      // 隐藏此路口的人行横道线，由相位控制颜色
      let whereClause = "";
      for (let crossId of this.crossSignalPhaseMap.keys()) {
        whereClause += `FEATUREID <> '${crossId}' and `;
      }
      whereClause = whereClause.substr(0, whereClause.length - 5);
      this.sidewalkLayer.definitionExpression = whereClause;

      const query = this.sidewalkLayer.createQuery();
      query.where = `FEATUREID = '${crossId}'`;
      query.outFields = ["*"];
      const result = await this.sidewalkLayer.queryFeatures(query);
      crossSignalPhase.sidewalkGraphics = result.features;
    } else {
      crossSignalPhase = this.crossSignalPhaseMap.get(
        crossId
      ) as CrossSignalPhase;
    }

    return crossSignalPhase.setSignalPhase(params);
  }

  public stopSignalPhase(crossId: string) {
    const crossSignalPhase = this.crossSignalPhaseMap.get(crossId);
    if (crossSignalPhase) {
      crossSignalPhase.stopSignalPhase();
    }
  }
}
